// src/components/sections/PageCards.jsx
import React, { useEffect, useRef, useState } from 'react'
import { motion, useInView } from 'framer-motion'
import { useAppSelector } from '../../hooks/useAppDispatch.js'
import { LazyImage } from '../common/LazyImage.jsx'
import './PageCards.scss'

export default function PageCards() {
  const { cards, loading } = useAppSelector((state) => state.global)

  const containerRef = useRef(null)
  const row1Ref = useRef(null)
  const row2Ref = useRef(null)
  const [isMobile, setIsMobile] = useState(false)

  const isRow1InView = useInView(row1Ref, { once: true, margin: '-50% 0px' })
  const isRow2InView = useInView(row2Ref, { once: true, margin: '-40% 0px' })

  // Detect mobile on mount
  useEffect(() => {
    const checkIfMobile = () => window.innerWidth <= 1024

    setIsMobile(checkIfMobile())

    const handleResize = () => {
      setIsMobile(checkIfMobile())
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Skeleton loading (functionality same, just dark section now)
  if (loading) {
    return (
      <section className="cards-section">
        <div className="tilted-cards-container">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse bg-[#151515f5] rounded-xl p-6 border border-white/10">
                <div className="h-12 w-12 bg-black-200 rounded-lg mb-4" />
                <div className="h-6 bg-black-200 rounded w-2/3 mb-2" />
                <div className="h-4 bg-black-200 rounded w-3/4" />
              </div>
            ))}
          </div>
        </div>
      </section>
    )
  }

  // No data = nothing
  if (!cards || cards.length === 0) {
    return null
  }

  // Split into two rows max 3 each (like v1)
  const firstRowCards = cards.slice(0, 3)
  const secondRowCards = cards.slice(3, 6)

  const desktopContainerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const desktopCenterCardVariants = {
    hidden: { y: 100, opacity: 0 },
    show: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 70,
        damping: 15,
      },
    },
  }

  const desktopSideCardVariants = {
    hidden: { x: -230, opacity: 0, scale: 0.9 },
    showLeft: {
      x: -480,
      y: 50,
      opacity: 1,
      rotate: -8,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 80,
        damping: 15,
        delay: 0.2,
      },
    },
    showRight: {
      x: 150,
      y: 50,
      opacity: 1,
      rotate: 8,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 80,
        damping: 15,
        delay: 0.2,
      },
    },
  }

  const desktopCardHover = {
    scale: 1.02,
    y: -15,
    transition: {
      stiffness: 100,
    },
  }

  const desktopCardHoverRight = {
    rotate: 6,
    y: 0,
    transition: {
      stiffness: 100,
    },
  }

  const desktopCardHoverLeft = {
    rotate: -6,
    y: 0,
    transition: {
      stiffness: 100,
    },
  }

  const mobileCardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.9,
        ease: 'easeOut',
      },
    },
  }

  const mobileContainerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const mobileCardHover = {
    scale: 1.03,
    y: -5,
    transition: {
      duration: 0.2,
    },
  }

  // Decide position class for desktop row card
  const getPositionClass = (index, length) => {
    if (length === 1) return 'center'
    if (length === 2) return index === 0 ? 'left' : 'right'
    // length >= 3
    if (index === 0) return 'left'
    if (index === 1) return 'center'
    return 'right'
  }

  const getVariantKey = (position) => {
    if (position === 'center') return 'show'
    if (position === 'left') return 'showLeft'
    return 'showRight'
  }

  const getHoverVariant = (position) => {
    if (position === 'center') return desktopCardHover
    if (position === 'left') return desktopCardHoverLeft
    return desktopCardHoverRight
  }

  return (
    <section className="cards-section">
      <div className="tilted-cards-container" ref={containerRef}>
        {isMobile ? (
          // Mobile layout
          <motion.div
            className="mobile-cards-grid"
            variants={mobileContainerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-20% 0px' }}
          >
            {cards.map((card, index) => (
              <motion.div
                key={index}
                className="mobile-card"
                variants={mobileCardVariants}
                transition={{ delay: index * 0.1 }}
                whileHover={mobileCardHover}
              >
                <div className="mobile-card-icon">
                  <LazyImage
                    src={card.imageUrl}
                    alt={card.title}
                    className="w-9 h-9 object-contain"
                    placeholder={
                      <div className="w-9 h-9 bg-black-200 rounded-lg" />
                    }
                  />
                </div>
                <div className="mobile-card-content">
                  <h3>{card.title}</h3>
                  <p>{card.subtitle}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <>
            {/* First row */}
            {firstRowCards.length > 0 && (
              <motion.div
                className="cards-row"
                ref={row1Ref}
                variants={desktopContainerVariants}
                initial="hidden"
                animate={isRow1InView ? 'show' : 'hidden'}
              >
                {firstRowCards.map((card, index) => {
                  const position = getPositionClass(index, firstRowCards.length)
                  const variantKey = getVariantKey(position)
                  const hoverVariant = getHoverVariant(position)

                  return (
                    <motion.div
                      key={index}
                      className={`tilted-card ${position}`}
                      variants={
                        position === 'center'
                          ? desktopCenterCardVariants
                          : desktopSideCardVariants
                      }
                      animate={isRow1InView ? variantKey : 'hidden'}
                      whileHover={hoverVariant}
                    >
                      <div className="card-icon">
                        <LazyImage
                          src={card.imageUrl}
                          alt={card.title}
                          className="w-full h-full object-contain"
                          placeholder={
                            <div className="w-full h-full bg-black-200 rounded-lg" />
                          }
                        />
                      </div>
                      <h3>{card.title}</h3>
                      <p>{card.subtitle}</p>
                    </motion.div>
                  )
                })}
              </motion.div>
            )}

            {/* Second row */}
            {secondRowCards.length > 0 && (
              <motion.div
                style={{ marginTop: '-50px' }}
                ref={row2Ref}
                className="cards-row"
                variants={desktopContainerVariants}
                initial="hidden"
                animate={isRow2InView ? 'show' : 'hidden'}
                transition={{ delayChildren: 1 }}
              >
                {secondRowCards.map((card, index) => {
                  const position = getPositionClass(index, secondRowCards.length)
                  const variantKey = getVariantKey(position)
                  const hoverVariant = getHoverVariant(position)

                  return (
                    <motion.div
                      key={index}
                      className={`tilted-card ${position}`}
                      variants={
                        position === 'center'
                          ? desktopCenterCardVariants
                          : desktopSideCardVariants
                      }
                      animate={isRow2InView ? variantKey : 'hidden'}
                      whileHover={hoverVariant}
                    >
                      <div className="card-icon">
                        <LazyImage
                          src={card.imageUrl}
                          alt={card.title}
                          className="w-full h-full object-contain"
                          placeholder={
                            <div className="w-full h-full bg-black-200 rounded-lg" />
                          }
                        />
                      </div>
                      <h3>{card.title}</h3>
                      <p>{card.subtitle}</p>
                    </motion.div>
                  )
                })}
              </motion.div>
            )}
          </>
        )}
      </div>
    </section>
  )
}
